import type {
  Breadcrumb,
  ErrorEvent,
} from '@sentry/angular';
import escapeRegExp from 'lodash/escapeRegExp';
import {Constants} from '../constants/constants';
import {SentryConstants} from '../constants/sentry/sentry-constants';
import {
  SentrySpan,
  SentryTransactionEvent,
} from '../../models/sentry.model';

const OPAQUE_ROUTE_MATCHERS: ReadonlyArray<readonly [RegExp, string]> = SentryConstants.OPAQUE_ROUTES_MOST_SPECIFIC_FIRST
  .map((template: string): readonly [RegExp, string] => [compileRouteTemplate(template), template]);

/**
 * Method: Strip query, fragment, access keys and record ids from a URL or span name
 * @param value - URL, path or span description
 */
export function scrubSentryUrl(value: string): string {
  return hideOpaqueRouteParams(value.split(SentryConstants.QUERY_OR_FRAGMENT)[0])
    .replace(SentryConstants.PREFIXED_ENGINE_ID, `${Constants.SLASH}${SentryConstants.ID_PLACEHOLDER}`)
    .replace(SentryConstants.UUID, SentryConstants.ID_PLACEHOLDER)
    .replace(SentryConstants.NUMERIC_SEGMENT, `${Constants.SLASH}${SentryConstants.ID_PLACEHOLDER}`);
}

/**
 * Method: Scrub the page URL and the transaction name of an event before it leaves the browser
 * @param event - Error or transaction event as the SDK serialises it
 */
export function scrubSentryEvent<T extends ErrorEvent | SentryTransactionEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubSentryUrl(event.request.url);
  }
  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubSentryUrl(event.request.headers.Referer);
  }
  if (event.transaction && event.type !== SentryConstants.TRANSACTION_EVENT_TYPE) {
    event.transaction = scrubSentryUrl(event.transaction);
  } else if (event.transaction && event.transaction_info?.source === SentryConstants.TRANSACTION_SOURCE_URL) {
    event.transaction = withTrailingSlash(scrubSentryUrl(event.transaction));
  }
  return event;
}

/**
 * Method: Scrub the name, URL and element attributes of a span, dropping its query and fragment attributes
 * @param span - Root or child span as the SDK serialises it
 */
export function scrubSentrySpan(span: SentrySpan): SentrySpan {
  if (span.description) {
    span.description = SentryConstants.SELECTOR_NAMED_SPAN_OP.test(span.op ?? '')
      ? scrubElementSelector(span.description)
      : scrubSentryUrl(span.description);
  }
  Object.entries(span.data).forEach(([attribute, value]: [string, unknown]): void => {
    if (typeof value === 'string' && SentryConstants.SPAN_URL_ATTRIBUTES.includes(attribute)) {
      span.data[attribute] = scrubSentryUrl(value);
    } else if (typeof value === 'string' && SentryConstants.SPAN_SELECTOR_ATTRIBUTE.test(attribute)) {
      span.data[attribute] = scrubElementSelector(value);
    }
  });
  SentryConstants.SPAN_QUERY_ATTRIBUTES.forEach((attribute: string): void => {
    delete span.data[attribute];
  });
  return span;
}

/**
 * Method: Scrub the navigation and request URLs and the clicked element carried by a breadcrumb
 * @param breadcrumb - Breadcrumb as the SDK records it
 */
export function scrubSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.message && breadcrumb.category?.startsWith(SentryConstants.SELECTOR_BREADCRUMB_CATEGORY_PREFIX)) {
    breadcrumb.message = scrubElementSelector(breadcrumb.message);
  }
  const data = breadcrumb.data;
  if (!data) {
    return breadcrumb;
  }
  SentryConstants.BREADCRUMB_URL_FIELDS.forEach((field: string): void => {
    if (typeof data[field] === 'string') {
      data[field] = scrubSentryUrl(data[field]);
    }
  });
  return breadcrumb;
}

/**
 * Method: Keep a selector up to its first attribute and mask element ids, as unescaped values can mimic any syntax
 * @param selector - Element path as the SDK serialises it
 */
function scrubElementSelector(selector: string): string {
  const firstAttribute = selector.indexOf(Constants.BRACKET_LEFT);
  const structure = firstAttribute === -1 ? selector : selector.slice(0, firstAttribute);
  return structure.replace(SentryConstants.ELEMENT_ID, `${Constants.HASHTAG}${SentryConstants.ID_PLACEHOLDER}`);
}

/**
 * Method: Replace the first listed route a path matches by its template, so each parameter shows by name
 * @param path - URL, path or span description already cut at its query
 */
function hideOpaqueRouteParams(path: string): string {
  const matcher = OPAQUE_ROUTE_MATCHERS.find(([pattern]: readonly [RegExp, string]): boolean => pattern.test(path));
  return matcher ? path.replace(matcher[0], matcher[1]) : path;
}

/**
 * Method: Turn a route template into a pattern that takes any single segment where the template has a parameter
 * @param template - Route path with a colon before each parameter name
 */
function compileRouteTemplate(template: string): RegExp {
  const pattern = template.split(Constants.SLASH)
    .map((segment: string): string => (segment.startsWith(Constants.COLON)
      ? SentryConstants.ROUTE_PARAM_SEGMENT
      : escapeRegExp(segment)))
    .join(Constants.SLASH);
  return new RegExp(`${pattern}${SentryConstants.ROUTE_SEGMENT_END}`);
}

/**
 * Method: Add the trailing slash route-named transactions carry, so a static page groups under one name
 * @param path - Transaction name already stripped of query and ids
 */
function withTrailingSlash(path: string): string {
  return path.endsWith(Constants.SLASH) ? path : `${path}${Constants.SLASH}`;
}
